import { Button } from '@ps-design-system/components/Button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@ps-design-system/components/Dialog';
import { Spinner } from '@ps-design-system/components/Spinner';
import { Text } from '@ps-design-system/components/Text';
import PropTypes from 'prop-types';
import React from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import { fetchCalendarPricing } from '../../services/checkout-service';
import { PricingRulesSummary } from './PricingRulesSummary';

export const PricingRulesDialog = injectIntl(
  ({
    isOpen,
    setIsOpen,
    pricingRules,
    currency,
    intl,
    accessToken,
    listingId
  }) => {
    const [isLoading, setIsLoading] = React.useState(false);
    const [errorMsg, setErrorMsg] = React.useState('');
    const [calendarPricingRules, setCalendarPricingRules] =
      React.useState(null);

    React.useEffect(() => {
      if (!isOpen || !!calendarPricingRules) {
        return;
      }

      (async () => {
        try {
          setIsLoading(true);
          const calendarPricingRes = await fetchCalendarPricing(
            accessToken,
            listingId
          );

          setCalendarPricingRules(calendarPricingRes);
          setIsLoading(false);
        } catch (error) {
          setIsLoading(false);
          setErrorMsg(
            intl.formatMessage({
              id: 'pricing_rules_dialog.something_went_wrong',
              defaultMessage: 'Something went wrong, please try again.'
            })
          );
        }
      })();
    }, [isOpen, calendarPricingRules]);

    return (
      <Dialog
        open={isOpen}
        onOpenChange={setIsOpen}
        data-testingid="add-ons-dialog"
      >
        <DialogContent
          style={{
            maxHeight: '75vh',
            minHeight: '400px'
          }}
        >
          <DialogHeader>
            <DialogTitle style={{ margin: 0 }}>
              <FormattedMessage
                id="pricing_rules_dialog.pricing_rules"
                defaultMessage="Pricing rules"
              />
            </DialogTitle>
          </DialogHeader>

          <DialogBody isScrollable={true}>
            {isLoading ? (
              <div
                style={{
                  minHeight: '200px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <Spinner appearance="black" size="large" />
              </div>
            ) : errorMsg ? (
              <Text className="tw-text-red-800">{errorMsg}</Text>
            ) : (
              <PricingRulesSummary
                pricingRules={pricingRules}
                calendarPricingRules={calendarPricingRules}
                currency={currency}
              />
            )}
          </DialogBody>

          <DialogFooter>
            <Button appearance="purple" onClick={() => setIsOpen(false)}>
              <FormattedMessage
                id="booking_request_error_dialog.okay"
                defaultMessage="Okay"
              />
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }
);
PricingRulesDialog.displayName = 'PricingRulesDialog';
PricingRulesDialog.propTypes = {
  isOpen: PropTypes.bool,
  setIsOpen: PropTypes.func,
  pricingRules: PropTypes.any,
  currency: PropTypes.string,
  intl: PropTypes.object,
  accessToken: PropTypes.string,
  listingId: PropTypes.string
};
